import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { X, ArrowRight } from "lucide-react";
import { completeOnboarding } from "@/lib/afritask";

/**
 * Lightweight 4-step spotlight walkthrough. Attaches to elements by data-testid.
 * If a target is missing, gracefully falls back to centering the tip.
 */
const STEPS = [
  {
    target: "pipeline-board",
    title: "Welcome to AfriTask AI",
    body: "This is your challenge pipeline — tasks move across columns as you enter rounds, get selected, submit, and get paid.",
  },
  {
    target: "topbar-points-chip",
    title: "Your Work Points",
    body: "Top up to enter rounds — ₦1,000 = 100 pts, with bonuses on ₦5k / ₦10k / ₦25k top-ups.",
  },
  {
    target: "welcome-streak",
    title: "Your stats matter",
    body: "Your streak, success rate, and earnings all boost your selection chances in future rounds.",
  },
  {
    target: "topbar-bell",
    title: "Stay in the loop",
    body: "The bell shows notifications: selection wins, submission reviews, badge unlocks, and payouts.",
  },
];

const STORAGE_KEY = "afritask.onboarding_seen";

export default function OnboardingTour({ me }) {
  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(false);
  const [rect, setRect] = useState(null);

  const shouldShow = useMemo(() => {
    if (!me) return false;
    if (localStorage.getItem(STORAGE_KEY) === "1") return false;
    if (me.completed_onboarding) return false;
    return true;
  }, [me]);

  useEffect(() => {
    if (!shouldShow) return;
    const t = setTimeout(() => setVisible(true), 800);
    return () => clearTimeout(t);
  }, [shouldShow]);

  useEffect(() => {
    if (!visible) return;
    const target = STEPS[step].target;
    const el = document.querySelector(`[data-testid="${target}"]`);
    if (el) {
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    } else {
      setRect(null);
    }
  }, [step, visible]);

  const finish = async (skipped = false) => {
    localStorage.setItem(STORAGE_KEY, "1");
    setVisible(false);
    try {
      await completeOnboarding();
    } catch { /* ignore */ }
    if (skipped) return;
  };

  if (!visible) return null;

  const s = STEPS[step];
  const isLast = step === STEPS.length - 1;

  // Compute tooltip position
  let tipStyle = { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
  if (rect) {
    const spaceBelow = window.innerHeight - (rect.top + rect.height);
    const top = spaceBelow > 220 ? rect.top + rect.height + 12 : rect.top - 220;
    tipStyle = { top: `${Math.max(20, Math.min(window.innerHeight - 240, top))}px`, left: `${Math.max(20, Math.min(window.innerWidth - 340, rect.left))}px` };
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[1000]"
      role="dialog"
      aria-label="Onboarding tour"
      data-testid="onboarding-tour"
    >
      {/* Dim overlay with a spotlight hole */}
      <div
        className="absolute inset-0 bg-neutral-900/70 transition-all"
        style={
          rect
            ? {
                clipPath: `polygon(
                  0 0, 100% 0, 100% 100%, 0 100%, 0 0,
                  ${rect.left - 8}px ${rect.top - 8}px,
                  ${rect.left - 8}px ${rect.top + rect.height + 8}px,
                  ${rect.left + rect.width + 8}px ${rect.top + rect.height + 8}px,
                  ${rect.left + rect.width + 8}px ${rect.top - 8}px,
                  ${rect.left - 8}px ${rect.top - 8}px
                )`,
              }
            : undefined
        }
      />
      {/* Spotlight ring */}
      {rect && (
        <div
          className="pointer-events-none absolute rounded-xl ring-4 ring-amber-600/80"
          style={{
            top: rect.top - 8,
            left: rect.left - 8,
            width: rect.width + 16,
            height: rect.height + 16,
          }}
        />
      )}
      {/* Tooltip */}
      <div
        className="absolute w-80 rounded-xl border border-neutral-200 bg-white p-4 shadow-2xl"
        style={tipStyle}
        data-testid={`tour-step-${step + 1}`}
      >
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-widest text-amber-600">
              Step {step + 1} of {STEPS.length}
            </p>
            <p className="mt-1 text-base font-bold text-neutral-900">{s.title}</p>
          </div>
          <button
            className="rounded p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
            onClick={() => finish(true)}
            aria-label="Skip tour"
            data-testid="tour-skip"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="mt-2 text-sm text-neutral-600">{s.body}</p>
        <div className="mt-4 flex items-center justify-between">
          <div className="flex gap-1">
            {STEPS.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 w-6 rounded-full ${i <= step ? "bg-amber-700" : "bg-neutral-200"}`}
              />
            ))}
          </div>
          {isLast ? (
            <Button size="sm" onClick={() => finish(false)} data-testid="tour-finish" className="bg-amber-700 hover:bg-amber-800">
              Got it
            </Button>
          ) : (
            <Button size="sm" onClick={() => setStep((s) => s + 1)} data-testid="tour-next" className="bg-amber-700 hover:bg-amber-800">
              Next <ArrowRight className="ml-1 h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
